import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';
import { GrowthBookService } from './growthbook/service';
import { ExperimentProps } from './growthbook/types';

/** GrowthBook feature key for the Buy Now button multi-variant experiment */
export const TRIAL_BANNER_BUY_NOW_EXPERIMENT_KEY = 'trial-banner-buy-now-experiment';

/**
 * Multi-variant split test for the header Buy Now button.
 * - Variant A (Control): No Buy Now button.
 * - Variant B: Buy Now button shown.
 * - Variant C: Buy Now button shown.
 */
@Injectable({
  providedIn: 'root',
})
export class TrialBannerBuyNowExperimentService {
  private readonly defaultVariant: ExperimentProps['variationid'] = 'A';

  readonly variant$ = this.growthBook.getFeature<ExperimentProps>(TRIAL_BANNER_BUY_NOW_EXPERIMENT_KEY);

  /** True when the user should see the Buy Now button (variants B or C). */
  readonly showBuyNowButton$: Observable<boolean>;

  constructor(private readonly growthBook: GrowthBookService) {
    this.showBuyNowButton$ = this.variant$.pipe(
      map((v) => {
        const variant = this.parseVariant(v);
        return variant === 'B' || variant === 'C';
      }),
      distinctUntilChanged(),
    );
  }

  getVariant(): ExperimentProps['variationid'] {
    const result = this.growthBook.getFeatureValue<ExperimentProps>(TRIAL_BANNER_BUY_NOW_EXPERIMENT_KEY);
    return this.parseVariant(result);
  }

  shouldShowBuyNowButton(): boolean {
    const v = this.getVariant();
    return v === 'B' || v === 'C';
  }

  private parseVariant(result: ExperimentProps | null | undefined): ExperimentProps['variationid'] {
    if (result?.variationid === 'A' || result?.variationid === 'B' || result?.variationid === 'C') {
      return result.variationid;
    }
    return this.defaultVariant;
  }
}
